import React, { useState } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Editor from '@monaco-editor/react';

const AddShard = ({ isOpen, onClose, onConfirm }) => {
  const { user } = useSession();
  const [formData, setFormData] = useState({
    shard: '',
    description: '',
    shard_key: '',
    shard_status: 'Draft',
    promptArea: ''
  });
  const [saving, setSaving] = useState(false);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save
  const handleSave = async () => {
    // Basic validation
    if (!formData.shard_key || !formData.description || !formData.promptArea) {
      alert('Please fill in all required fields (Shard Key, Description, and Prompt Area).');
      return;
    }
    
    try {
      setSaving(true);
      
      const requestBody = {
        shard: formData.shard,
        description: formData.description,
        shard_key: formData.shard_key,
        shard_status: formData.shard_status,
        promptArea: formData.promptArea
      };
      
      console.log('Creating shard:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.CREATE_SHARD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Shard created successfully:', data);
      
      // Call the onConfirm callback to refresh the shards list
      onConfirm();
      
      // Reset form and close modal
      setFormData({
        shard: '',
        description: '',
        shard_key: '',
        shard_status: 'Draft',
        promptArea: ''
      });
      onClose();
      
    } catch (err) {
      console.error('Error creating shard:', err);
      alert('Failed to create shard. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setFormData({
      shard: '',
      description: '',
      shard_key: '',
      shard_status: 'Draft',
      promptArea: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[90%] h-[90%] max-w-6xl flex flex-col">
        {/* Panel Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h4 className="text-xl font-semibold text-gray-800">Add New Shard</h4>
            <p className="text-sm text-gray-600">Create a new prompt shard component</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                !saving
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Creating...' : 'Create Shard'}
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 flex p-6 gap-6 min-h-0">
          {/* Left Side - Form Fields */}
          <div className="w-1/3 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shard Key <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.shard_key}
                onChange={(e) => handleFieldChange('shard_key', e.target.value)}
                placeholder="e.g., general.guidelines"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={4}
                placeholder="Brief description of what this shard does..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Area <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.promptArea}
                onChange={(e) => handleFieldChange('promptArea', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Prompt Area</option>
                <option value="General">General</option>
                <option value="Criteria">Criteria</option>
                <option value="Evaluation">Evaluation</option>
                <option value="Questionnaire">Questionnaire</option>
                <option value="Challenge">Challenge</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={formData.shard_status}
                onChange={(e) => handleFieldChange('shard_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Right Side - Shard Editor */}
          <div className="flex-1 flex flex-col">
            <label className="block text-sm font-medium text-gray-700 mb-2">Shard Content</label>
            <div className="flex-1 border border-gray-300 rounded-md overflow-hidden">
              <Editor
                height="100%"
                defaultLanguage="markdown"
                value={formData.shard}
                onChange={(value) => handleFieldChange('shard', value || '')}
                options={{
                  readOnly: false,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 14,
                  lineNumbers: 'off',
                  folding: true,
                  automaticLayout: true
                }}
                theme="vs"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddShard;
